/**
 * OE type — mirrors the `type` column's check constraint.
 *
 * The order is the order of the tree: root, company, profit centre,
 * department. `ORGANIZATION_UNIT_TYPES` is what the validator, the check
 * constraint and the editor's dropdown all read, so a fifth type is one line
 * here and one in the constraint.
 *
 * A station was a fifth type until 2026-10-27; it is a `StationDto` now,
 * held by an OE the way a position is.
 */
export const ORGANIZATION_UNIT_TYPES = [
  'organization',
  'company',
  'profit_center',
  'department',
] as const;

export type OrganizationUnitType = (typeof ORGANIZATION_UNIT_TYPES)[number];

export interface OrganizationDto {
  id: string;
  name: string;
  createdAt?: Date | string;
  updatedAt?: Date | string | null;
  /** Persons made admin/owner of the org (assigned to the root OE's Owner position). */
  ownerPersonIds?: string[];
}

export interface OrganizationUnitDto {
  id: string;
  name: string;
  type: OrganizationUnitType;
  organizationId: string;
  parentId: string | null;
  description: string | null;
  legalName: string | null;
  countryCode: string | null;
  /**
   * IANA zone the wall-clock times of this OE mean, e.g. `Europe/Vienna`.
   * Empty means "same as the parent" — a rule saying "not before 08:30" is
   * read in whichever zone the nearest ancestor names.
   */
  timezone: string | null;
  /**
   * Holiday entitlement, inherited like the zone: null takes what the nearest
   * ancestor names. The waiting period is in months, 0 meaning "from the first
   * day"; the year type is `WORK_YEAR` or `CALENDAR_YEAR`.
   */
  vacationWaitingPeriodMonths: number | null;
  vacationYearType: string | null;
  isDeletionLocked: boolean;
}

export interface PositionDto {
  id: string;
  name: string;
  organizationUnitId: string;
  description: string | null;
}

/** A station — a place of work inside an OE, next to its positions. */
export interface StationDto {
  id: string;
  name: string;
  organizationUnitId: string;
  description: string | null;
  /** Any person of the organization, not only of the subtree. */
  responsiblePersonId: string | null;
}

/** A person assigned to a position (active assignment). */
export interface PositionAssigneeDto {
  personPositionId: string;
  personId: string;
  firstName: string;
  lastName: string;
  isPrimary: boolean;
}

export interface OrgTreePositionDto {
  id: string;
  name: string;
  description: string | null;
  roleIds: string[];
  assignees: PositionAssigneeDto[];
  /** Whether the actor may manage this position (edit, roles, assign/move persons). */
  canManage: boolean;
  /** Whether the actor may create an OE beneath this position. */
  canCreateChild: boolean;
  /** Position is manageable and has neither assignees nor child OEs. */
  canDelete: boolean;
}

export interface OrgTreeStationDto {
  id: string;
  name: string;
  description: string | null;
  responsiblePersonId: string | null;
  /**
   * Their name, carried along so the row and the form can show who it is
   * without a second request. Null whenever the id is.
   */
  responsiblePersonName: string | null;
  /** Whether the actor may edit this station and its skill requirements. */
  canManage: boolean;
  /** Whether the actor may delete it. */
  canDelete: boolean;
}

/** Recursive OE node for the organigram. */
export interface OrgTreeNodeDto {
  id: string;
  name: string;
  type: OrganizationUnitType;
  description: string | null;
  legalName: string | null;
  countryCode: string | null;
  /** Empty means the OE inherits its parent's zone. */
  timezone: string | null;
  /**
   * Holiday entitlement, inherited like the zone: null takes what the nearest
   * ancestor names. The waiting period is in months, 0 meaning "from the first
   * day"; the year type is `WORK_YEAR` or `CALENDAR_YEAR`.
   */
  vacationWaitingPeriodMonths: number | null;
  vacationYearType: string | null;
  isDeletionLocked: boolean;
  positions: OrgTreePositionDto[];
  stations: OrgTreeStationDto[];
  children: OrgTreeNodeDto[];
  /**
   * True for ancestor OEs shown only to give the in-scope subtree a path to the
   * root: rendered greyed/read-only, no positions, no stations, no actions.
   */
  contextOnly: boolean;
  /** The actor may edit this OE (organizationUnit.update covers it). */
  canUpdate: boolean;
  /** The actor may add a child OE / position under it (organizationUnit.create). */
  canCreateChild: boolean;
  /** The actor may delete this OE (organizationUnit.delete + not locked/root/non-empty). */
  canDelete: boolean;
}

export interface OrgTreeResponse {
  organization: OrganizationDto;
  tree: OrgTreeNodeDto | null;
}

/** Result of moving a person's assignment to another position. */
export interface MovePersonPositionResult {
  personPositionId: string;
  personId: string;
  positionId: string;
  isActive: true;
}
